import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { locationsAPI, usersAPI } from '../../services/api';
import { Location, User, UserRole } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
  ArrowLeftIcon,
  PlusIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';

const LocationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [location, setLocation] = useState<Location | null>(null);
  const [employees, setEmployees] = useState<User[]>([]);
  const [availableEmployees, setAvailableEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigningEmployee, setAssigningEmployee] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  useEffect(() => {
    if (id) {
      fetchLocation();
      fetchEmployees();
    }
  }, [id]);

  const fetchLocation = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await locationsAPI.getById(id);
      setLocation(data);
    } catch (err: any) {
      console.error('Error fetching location:', err);
      setError(err.response?.data?.message || 'Failed to fetch location');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const allUsers = await usersAPI.getAll();
      const locationEmployees = allUsers.filter(user => user.locationId === id);
      const unassignedEmployees = allUsers.filter(user => 
        !user.locationId && (user.role === UserRole.EMPLOYEE || user.role === UserRole.BRANCH_MANAGER)
      );
      
      setEmployees(locationEmployees);
      setAvailableEmployees(unassignedEmployees);
    } catch (err: any) {
      console.error('Error fetching employees:', err);
    }
  };

  const handleDelete = async () => {
    if (!location || !window.confirm(t('common.confirmDelete'))) {
      return;
    }

    try {
      await locationsAPI.delete(location.id);
      navigate('/locations');
    } catch (err: any) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.message || 'Failed to delete location');
    }
  };

  const handleAssignEmployee = async () => {
    if (!selectedEmployeeId || !location) return;

    try {
      setAssigningEmployee(true);
      await locationsAPI.assignEmployee(location.id, selectedEmployeeId);
      setSelectedEmployeeId('');
      await fetchEmployees();
    } catch (err: any) {
      console.error('Error assigning employee:', err);
      setError(err.response?.data?.message || 'Failed to assign employee');
    } finally {
      setAssigningEmployee(false);
    }
  };

  const handleUnassignEmployee = async (employeeId: string) => {
    if (!window.confirm(t('locations.confirmUnassignEmployee'))) {
      return;
    }

    try {
      await locationsAPI.unassignEmployee(employeeId);
      await fetchEmployees();
    } catch (err: any) {
      console.error('Error unassigning employee:', err);
      setError(err.response?.data?.message || 'Failed to unassign employee');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
        {error || 'Location not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/locations"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-primary-600 mr-3" />
              {location.name}
            </h1>
            <div className="flex items-center mt-2">
              {location.isActive ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-green-600 font-medium">Active</span>
                </>
              ) : (
                <>
                  <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-500 font-medium">Inactive</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/locations/${location.id}/edit`}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Edit</span>
          </Link>
          <button
            onClick={handleDelete}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <TrashIcon className="h-5 w-5" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Location Information */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Location Details</h2>
          </div>
          <div className="p-6 space-y-4">
            {location.address && (
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Address</span>
                  <p className="text-gray-900">{location.address}</p>
                </div>
              </div>
            )}
            
            {location.phone && (
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Phone</span>
                  <p className="text-gray-900">{location.phone}</p>
                </div>
              </div>
            )}

            {location.email && (
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-700">Email</span>
                  <p className="text-gray-900">{location.email}</p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Manager</span>
                <p className="text-gray-900">
                  {location.manager 
                    ? `${location.manager.firstName} ${location.manager.lastName}` 
                    : 'No manager assigned'
                  }
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">
                  {new Date(location.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900">
                  {new Date(location.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Employees */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <UsersIcon className="h-5 w-5 mr-2" />
                {t('locations.employees.count', { count: employees.length })}
              </h2>
            </div>
          </div>
          
          {/* Assign Employee Form */}
          {availableEmployees.length > 0 && (
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">{t('locations.sections.assignEmployee')}</h3>
              <div className="flex space-x-2">
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">{t('locations.hints.selectEmployee')}</option>
                  {availableEmployees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName} ({employee.role})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignEmployee}
                  disabled={!selectedEmployeeId || assigningEmployee}
                  className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  {assigningEmployee ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <PlusIcon className="h-4 w-4" />
                  )}
                  <span className="text-sm">{t('locations.actions.assign')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Employees List */}
          <div className="p-6">
            {employees.length === 0 ? (
              <div className="text-center py-6">
                <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('locations.employees.none')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {employees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <UserIcon className="h-4 w-4 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{employee.role}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnassignEmployee(employee.id)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                      title={t('locations.employees.unassignTitle')}
                    >
                      <UserMinusIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationDetail;
